import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API, getOrCreateUserId, fetchOrCreateProfile, getStoredName } from "../lib/api";
import { sfx } from "../lib/sound";
import { mountBanner, unmountBanner, shouldShowBanner, BANNER_HEIGHT_PX, isAdsRemovedActive } from "../lib/banner";

export default function Tournaments() {
  const nav = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(null); // tournament id currently joining
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try { setProfile(await fetchOrCreateProfile(getStoredName())); } catch (e) { console.error(e); }
      try {
        const res = await axios.get(`${API}/tournaments`);
        setTournaments(res.data.tournaments || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Banner ad on a passive screen.
  useEffect(() => {
    if (isAdsRemovedActive(profile)) { unmountBanner(); return undefined; }
    if (!shouldShowBanner()) return undefined;
    mountBanner();
    return () => { unmountBanner(); };
  }, [profile?.ads_removed_until]);

  const join = async (id) => {
    if (!profile) return;
    setBusy(id); setError("");
    try {
      const user_id = getOrCreateUserId();
      const res = await axios.post(`${API}/tournaments/${id}/join`, {
        user_id, name: profile.name, avatar: profile.avatar || "default",
      });
      sfx.click();
      nav(`/online/room/${res.data.code}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to join tournament.");
      setBusy(null);
    }
  };

  return (
    <div
      className="min-h-screen w-full px-4 md:px-8 py-8"
      style={{ paddingBottom: `${BANNER_HEIGHT_PX + 24}px` }}
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between gap-2 mb-6 flex-wrap">
          <div className="flex items-center gap-2 md:gap-3">
            <button className="btn-ghost" onClick={() => nav("/")} data-testid="tournaments-back-btn">Back</button>
            <h1 className="font-display font-black text-xl md:text-3xl tracking-tight">Tournaments</h1>
          </div>
          {profile && (
            <div className="flex items-center gap-1.5 md:gap-2">
              <div className="glass rounded-full px-2.5 py-1.5 md:px-3 md:py-2 flex items-center gap-1.5" style={{ minHeight: 32 }}>
                <span style={{ color: "#FFD700", fontSize: 13 }}>◆</span>
                <span className="font-mono font-bold" style={{ fontSize: 12 }}>{profile.chips.toLocaleString()}</span>
              </div>
              <div className="glass rounded-full px-2.5 py-1.5 md:px-3 md:py-2 flex items-center gap-1.5"
                style={{ border: "1px solid rgba(0,229,255,0.45)", minHeight: 32 }}>
                <span style={{ color: "#00E5FF", fontSize: 13 }}>◈</span>
                <span className="font-mono font-bold" style={{ fontSize: 12 }}>{(profile.gems || 0).toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>

        <p className="text-white/65 text-sm mb-6 max-w-xl">
          Curated competitive heats. Pay the entry fee, beat 3 opponents, and walk away
          with the pot — plus a chip + gem bonus for victory.
          High-stakes tiers run as <b style={{ color: "#B537F2" }}>best-of-3 championships</b> for true bragging rights.
        </p>

        {error && (
          <div className="rounded-2xl px-4 py-3 mb-6 text-sm" style={{
            color: "#FF8A8A", border: "1px solid rgba(255,92,92,0.4)", background: "rgba(255,92,92,0.06)",
          }} data-testid="tournament-error">{error}</div>
        )}

        <div className="grid md:grid-cols-3 gap-4" data-testid="tournament-grid">
          {tournaments.map((t) => {
            const canAfford = (profile?.chips || 0) >= (t.entry_fee || 0);
            const pot = (t.entry_fee || 0) * t.max_players;
            return (
              <motion.div
                key={t.id}
                whileHover={{ y: -4 }}
                className="glass rounded-3xl p-5 flex flex-col"
                style={{
                  border: `1.5px solid ${t.theme_color}55`,
                  boxShadow: `0 0 24px ${t.theme_color}22`,
                }}
                data-testid={`tournament-${t.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span style={{ fontSize: 22 }}>{t.icon}</span>
                  <h3 className="font-display font-black text-xl" style={{ color: t.theme_color }}>{t.name}</h3>
                </div>
                {t.series_target > 1 && (
                  <div className="label-eyebrow mb-2" style={{ color: t.theme_color, fontSize: 9 }}>
                    BEST OF {t.series_target * 2 - 1} · CHAMPIONSHIP
                  </div>
                )}
                <div className="text-white/55 text-xs mb-4 min-h-[34px]">{t.tagline}</div>

                <div className="space-y-2 text-sm mb-5">
                  <div className="flex items-center justify-between">
                    <span className="text-white/55">Entry</span>
                    <span className="font-mono font-bold">
                      {t.entry_fee === 0 ? "FREE" : <><span style={{ color: "#FFD700" }}>◆</span> {t.entry_fee.toLocaleString()}</>}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/55">Pot</span>
                    <span className="font-mono font-bold">
                      {pot === 0 ? "—" : <><span style={{ color: "#FFD700" }}>◆</span> {pot.toLocaleString()}</>}
                    </span>
                  </div>
                  {t.bonus_chips > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-white/55">Champion chip bonus</span>
                      <span className="font-mono font-bold">
                        <span style={{ color: "#FFD700" }}>◆</span> +{t.bonus_chips.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {t.bonus_gems > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-white/55">Champion gem bonus</span>
                      <span className="font-mono font-bold">
                        <span style={{ color: "#00E5FF" }}>◈</span> {t.bonus_gems}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-white/55">Live</span>
                    <span className="text-xs">
                      {t.waiting_rooms} waiting · {t.playing_rooms} in play
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => join(t.id)}
                  disabled={!canAfford || busy === t.id}
                  className="mt-auto rounded-full px-4 py-3 font-display font-bold text-sm"
                  style={{
                    background: canAfford ? t.theme_color : "rgba(255,255,255,0.06)",
                    color: canAfford ? "#0B0C10" : "rgba(255,255,255,0.4)",
                    cursor: canAfford ? "pointer" : "not-allowed",
                    opacity: busy === t.id ? 0.6 : 1,
                  }}
                  data-testid={`join-${t.id}-btn`}
                >
                  {busy === t.id ? "Joining…" : (canAfford ? "ENTER" : "INSUFFICIENT CHIPS")}
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
